import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'node:querystring';
import styled from 'styled-components';
import { Row, Col, Button } from 'antd';

import { generateQueryFromFilter } from '../../common/helpers/searchService.utils';
import useI18n from '../../common/hooks/useI18n';
import useSearchService, {
  SearchFilter,
} from '../../common/hooks/useSearchService';
import { IFlat } from '../../common/model/flat.model';
import { IOffice } from '../../common/model/office.model';
import { FlatOrderBy } from '../../common/model/enums/flatOrderBy.enum';

import { Subtitle } from './';
import { FiltersModal } from '../search';
import {
  ResultsBottomSection,
  ResultsSection,
  ResultsTopSection,
  SearchBar,
} from '../shared';

type Props = {
  office: IOffice;
  flats: IFlat[];
  className?: string;
};

const SHOW_MORE_FLATS_COUNT = 8;

const SearchBarWrapper = styled.div`
  margin-top: 32px;
  margin-bottom: 16px;
  display: flex;
  justify-content: center;

  & > *:first-child {
    width: 67vw;
    max-width: 951px;
  }

  @media ${(props) => props.theme.breakpoints.xs} {
    & > *:first-child {
      width: 83vw;
    }
  }
`;

const ResultsWrapper = styled.div`
  padding-left: ${(props) => props.theme.grid.getGridColumns(1, 1)};
  padding-right: ${(props) => props.theme.grid.getGridColumns(1, 1)};

  @media ${(props) => props.theme.breakpoints.md} {
    padding-left: ${(props) => props.theme.grid.getGridColumns(4, 1)};
    padding-right: ${(props) => props.theme.grid.getGridColumns(4, 1)};
  }
  @media ${(props) => props.theme.breakpoints.smd} {
    padding-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
    padding-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  }
`;

const InfoSection = styled.div`
  margin-top: 24px;
  padding-bottom: 16px;

  @media ${(props) => props.theme.breakpoints.xs} {
    margin-top: 16px;
  }
`;

const LoadMoreButtonRow = styled(Row)`
  margin-top: 24px;
`;

const Gradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  background: linear-gradient(180deg, rgba(242, 242, 242, 0) 0%, #f2f2f2 67%);
  height: 32vh;
  width: 100%;
`;

const ShowMore = styled.div`
  position: absolute;
  bottom: 48px;
  left: 0;
  right: 0;
`;

const ShowMoreButton = styled(Button)`
  width: 100%;
`;

const OfficeFlatsSection: React.FC<Props> = ({ office, flats, className }) => {
  const router = useRouter();
  const i18n = useI18n();

  const searchService = useSearchService(flats, flats);
  const searchServiceRef = useRef(searchService);

  const [mounted, setMounted] = useState(false);
  const [autoCompleteValue, setAutoCompleteValue] = useState('');
  const [showAllFlats, setShowAllFlats] = useState(false);
  const [filtersModalVisible, setFiltersModalVisible] = useState(false);

  const flatsToShow = useMemo(() => {
    let result = searchService.getResults();

    return showAllFlats ? result : result.slice(0, SHOW_MORE_FLATS_COUNT);
  }, [searchService, showAllFlats]);

  const setQueryParams = useCallback(
    (queryParams: ParsedUrlQuery) => {
      router.push(
        {
          pathname: globalThis.location.pathname,
          query: { ...queryParams },
        },
        undefined,
        { shallow: true, scroll: false }
      );
    },
    [router]
  );

  const onSearchBarSearch = useCallback(
    (value: string) => {
      setQueryParams(
        generateQueryFromFilter({
          ...searchService.getFilter,
          q: value || undefined,
        })
      );
    },
    [searchService, setQueryParams]
  );

  const onFilterChange = useCallback(
    (filter: SearchFilter) => {
      const currentFilter = searchService.getFilter();
      const queryParams = generateQueryFromFilter({
        q: currentFilter.q,
        ...filter,
      });
      setQueryParams(queryParams);

      setFiltersModalVisible(false);
    },
    [searchService, setQueryParams]
  );

  const patchFilter = useCallback(
    (filter) => {
      onFilterChange({ ...searchService.getFilter(), ...filter });
    },
    [onFilterChange, searchService]
  );

  useEffect(() => {
    searchServiceRef.current.incrementPageSize(true);

    setMounted(true);
  }, []);

  // Cannot use useCallback here
  const hasFilters = () => {
    if (!mounted || globalThis.window === undefined) {
      return false;
    }

    const searchParams = new URLSearchParams(globalThis.window.location.search);
    return Array.from(searchParams.keys()).some(
      (k) => !['query', 'slug', 'city', 'zone', 'q'].includes(k)
    );
  };

  return (
    <div className={className}>
      <Subtitle
        text={i18n.t('officeDetail.messages.flatsTitle', {
          officeName: office.name,
        })}
      />

      <SearchBarWrapper>
        <SearchBar
          height="48px"
          value={autoCompleteValue}
          options={[]}
          onValueChange={setAutoCompleteValue}
          onSearch={onSearchBarSearch}
          onSelect={() => null}
          placeholder={i18n.t('officeDetail.messages.searchBarPlaceholder')}
          onFiltersButtonClick={() => {
            setFiltersModalVisible(true);
          }}
          applyFilter={patchFilter}
          hasFilters={hasFilters()}
        />
      </SearchBarWrapper>

      <ResultsWrapper>
        {searchService.getResultsCount() > 0 && (
          <ResultsTopSection
            resultsCount={searchService.getResultsCount()}
            orderBy={searchService.getOrderBy()}
            onOrderByChange={(orderBy) =>
              searchService.setOrderBy(orderBy as FlatOrderBy)
            }
          />
        )}

        <ResultsSection
          flats={flatsToShow}
          offices={[office]}
          xlSpan={12}
          cardBackgroundColor="white"
        />

        <InfoSection>
          <ResultsBottomSection
            pageSize={searchService.getPageSize()}
            resultsCount={searchService.getResultsCount()}
          />

          {searchService.getResultsCount() > 0 &&
            searchService.getPageSize() < searchService.getResultsCount() && (
              <LoadMoreButtonRow justify="center">
                <Col
                  xs={{ span: 20 }}
                  sm={{ span: 14 }}
                  md={{ span: 10 }}
                  lg={{ span: 7 }}
                  xl={{ span: 5 }}
                  xxl={{ span: 3 }}
                >
                  <ShowMoreButton
                    type="primary"
                    onClick={() => searchService.incrementPageSize()}
                  >
                    <span>{i18n.t('actions.showMore')}</span>
                  </ShowMoreButton>
                </Col>
              </LoadMoreButtonRow>
            )}
        </InfoSection>
      </ResultsWrapper>

      {showAllFlats || flats.length < SHOW_MORE_FLATS_COUNT ? null : (
        <Gradient>
          <ShowMore>
            <Row justify="center">
              <Col
                xs={{ span: 20 }}
                sm={{ span: 14 }}
                md={{ span: 10 }}
                lg={{ span: 7 }}
                xl={{ span: 5 }}
                xxl={{ span: 3 }}
              >
                <ShowMoreButton
                  type="primary"
                  onClick={() => {
                    setShowAllFlats(true);
                  }}
                >
                  <span>{i18n.t('actions.showMore')}</span>
                </ShowMoreButton>
              </Col>
            </Row>
          </ShowMore>
        </Gradient>
      )}

      <FiltersModal
        visible={filtersModalVisible}
        onOk={onFilterChange}
        onCancel={() => {
          setFiltersModalVisible(false);
        }}
      />
    </div>
  );
};

export default styled(OfficeFlatsSection)`
  background-color: ${(props) => props.theme.colors.grey};
  padding-top: 48px;
  padding-bottom: 48px;
  overflow: hidden;
  position: relative;
`;
